"use client";

import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./PostCard.css";
import PostContentIcon from "../../../../assets/icons/postcontent.png";
import likeIconOutline from "../../../../assets/icons/svg/postcard-like-outline.svg";
import likeIconFilled from "../../../../assets/icons/svg/postcard-like-filled.svg";
import commentIconPrimary from "../../../../assets/icons/svg/postcard-comment-primary.svg";
import commentIconMuted from "../../../../assets/icons/svg/postcard-comment-muted.svg";
import sendIcon from "../../../../assets/icons/svg/postcard-send.svg";

import { doctorProfilePath } from "../../../../utils/doctorProfilePath";
import useComments from "../../../hooks/useComments";
import useDoctorInformation, { resolveDoctorAvatar } from "../../../../ProfileDoctor/hooks/useDoctorInformation";
import useLikePost from "../../../hooks/useLikePost";
import { axiosDoctorRegister, axiosUsers } from "../../../../api/axios";
import ConfirmDeleteModal from "../../MedicalQuestionsPage/ConfirmDeleteModal/ConfirmDeleteModal";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

/* ---------- Helper timeAgo ---------- */
const timeAgo = (d) => {
  if (!d) return "";
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  const map = [
    ["y", 31536000],
    ["mo", 2592000],
    ["w", 604800],
    ["d", 86400],
    ["h", 3600],
    ["m", 60],
  ];
  for (const [k, v] of map) {
    const n = Math.floor(secs / v);
    if (n >= 1) return `${n}${k}`;
  }
  return "now";
};

const resolvePostKey = (p) => {
  if (!p) return null;
  return p.postId ?? p._id ?? p.id ?? null;
};

const safeStr = (v) => (v == null ? "" : String(v)).trim();
const isNumericId = (v) => /^\d+$/.test(String(v ?? "").trim());
const pickActorId = (u) => u?.id ?? u?._id ?? u?.userId ?? u?.pgId ?? u?.uuid ?? null;

const normId = (v) => {
  let s = safeStr(v);
  if (!s) return null;
  if (s.startsWith("DR_") || s.startsWith("dr_")) s = s.slice(3);
  if (s.startsWith("PAT_") || s.startsWith("pat_")) s = s.slice(4);
  s = s.trim();
  if (!s || ["nan", "undefined", "null"].includes(s.toLowerCase())) return null;
  return s;
};

const buildFullName = (obj) => {
  if (!obj) return "Member";
  const first = obj.firstName || obj.prenom || obj.name || obj.fullName || obj.displayName || "";
  const last = obj.lastName || obj.nom || "";
  const full = `${safeStr(first)} ${safeStr(last)}`.trim();
  return full || safeStr(first) || safeStr(last) || "Member";
};

const isAbsoluteUrl = (s) => /^https?:\/\//i.test(String(s || ""));
const isDataUrl = (s) => /^data:/i.test(String(s || ""));

const resolveDoctorAvatarSafe = (raw) => {
  const s = safeStr(raw);
  if (!s) return null;
  if (isAbsoluteUrl(s) || isDataUrl(s)) return s;
  return resolveDoctorAvatar(s) || s;
};

const resolvePatientAvatarSafe = (raw) => {
  const s = safeStr(raw);
  if (!s) return null;
  return s;
};

const extractAuthorIdAndRole = (c) => {
  const doctorId =
    normId(c?.doctorId) ||
    normId(c?.doctor?.id ?? c?.doctor?._id ?? c?.doctor?.doctorId ?? c?.doctor?.userId) ||
    null;

  const userId =
    normId(c?.userId) ||
    normId(c?.user?.id ?? c?.user?._id ?? c?.user?.userId ?? c?.user?.uuid) ||
    null;

  const authorId =
    normId(c?.authorId) ||
    normId(c?.fromUserId) ||
    normId(c?.senderId) ||
    normId(c?.createdById) ||
    null;

  const roleHint = safeStr(c?.user?.role || c?.role || c?.actorRole || "").toLowerCase();

  if (doctorId) return { id: doctorId, isDoctor: true };
  if (userId) {
    const doctorLike = roleHint.includes("doctor") || (isNumericId(userId) && !String(userId).includes("-"));
    return { id: userId, isDoctor: doctorLike };
  }
  if (authorId) {
    const doctorLike = roleHint.includes("doctor") || (isNumericId(authorId) && !String(authorId).includes("-"));
    return { id: authorId, isDoctor: doctorLike };
  }

  return { id: null, isDoctor: false };
};

const getInitials = (name, fallback = "DR") => {
  const clean = safeStr(name);
  if (!clean) return fallback;

  const normalized = clean.replace(/^dr\.?\s+/i, "").trim();
  const parts = normalized.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return fallback;
};

const formatDoctorName = (name) => {
  const clean = safeStr(name);
  if (!clean) return "Dr";
  if (/^dr\.?\s/i.test(clean)) return clean;
  return `Dr. ${clean}`;
};

const PostCard = ({
  doctorName = null,
  timeAgo: timeLabel = null,
  content = null,
  image = null,
  attachments = [],
  likes = 0,
  comments: commentsProp = 0,
  profileImage = null,
  isFirstPost = false,

  visibleToDoctorsOnly = false,

  postId: rawPostId = null,
  currentUserId = null,
  postOwnerId = null,
  createdAt = null,

  onEdit = null,
  onDelete = null,
  canManagePost = false,
  isSaved = false,
  onToggleSave = null,
  isSaving = false,
  isUpdating = false,
  isDeleting = false,

  documentFullBleed = true,
  documentViewportHeight = 90,
  documentHeight = 480,
  documentFitH = true,

  doctorsByPgId = {},
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const viewerIsDoctor = useSelector((state) => {
    const doctorSession = state?.doctor?.user;
    if (doctorSession) return true;
    const u =
      state?.auth?.user ||
      state?.patient?.user ||
      state?.user?.user ||
      state?.users?.currentUser ||
      null;
    const role = safeStr(u?.role || u?.type || "").toLowerCase();
    if (role.includes("doctor")) return true;
    if (u?.doctorId != null) return true;
    return false;
  });

  if (!viewerIsDoctor && visibleToDoctorsOnly === true) {
    return null;
  }

  const postKey = useMemo(() => {
    if (typeof rawPostId === "object" && rawPostId != null) return resolvePostKey(rawPostId);
    return rawPostId ?? null;
  }, [rawPostId]);

  const resolvedPostOwnerId = useMemo(() => {
    const raw = safeStr(postOwnerId);
    if (!raw) return null;
    if (isNumericId(raw)) return raw;
    const found = doctorsByPgId?.[raw];
    if (found) {
      return safeStr(found?.pgId ?? found?.userId ?? found?.id ?? raw);
    }
    return raw;
  }, [postOwnerId, doctorsByPgId]);

  const avatarUser = useMemo(() => ({ avatar: profileImage }), [profileImage]);
  const { avatarSrc } = useDoctorInformation(avatarUser);
  const headerAvatar = avatarSrc || profileImage || DEFAULT_AVATAR;

  const actorFromStore = useSelector((state) => {
    const candidate =
      state?.auth?.user ||
      state?.patient?.user ||
      state?.doctor?.user ||
      state?.user?.user ||
      state?.users?.user ||
      state?.users?.currentUser ||
      null;
    return pickActorId(candidate);
  });

  const effectiveActorId = normId(currentUserId ?? actorFromStore);

  const { liked, count: likeCount, fetchStatus, handleToggleLike } = useLikePost(
    postKey,
    effectiveActorId,
    postOwnerId
  );

  useEffect(() => {
    if (postKey && effectiveActorId != null) fetchStatus();
  }, [postKey, effectiveActorId, fetchStatus]);

  const [showInlineComments, setShowInlineComments] = useState(false);

  const {
    comments,
    isLoading: loadingComments,
    commentsCount,
    fetchComments,
    addComment,
    updateComment,
    deleteComment,
  } = useComments(postKey);

  const orderedComments = useMemo(() => {
    const list = Array.isArray(comments) ? [...comments] : [];
    return list.sort((a, b) => {
      const isDoctorA = extractAuthorIdAndRole(a).isDoctor;
      const isDoctorB = extractAuthorIdAndRole(b).isDoctor;
      if (isDoctorA !== isDoctorB) return isDoctorB ? 1 : -1;

      const dateA = new Date(a?.createdAt ?? a?.created_at ?? a?.date ?? 0).getTime();
      const dateB = new Date(b?.createdAt ?? b?.created_at ?? b?.date ?? 0).getTime();
      return (Number.isFinite(dateB) ? dateB : 0) - (Number.isFinite(dateA) ? dateA : 0);
    });
  }, [comments]);

  useEffect(() => {
    if (!postKey) return;
    fetchComments();
  }, [postKey, fetchComments]);

  useEffect(() => {
    if (showInlineComments && postKey) fetchComments();
  }, [showInlineComments, postKey, fetchComments]);

  /* =========================================================
     NAVIGATION PROFIL
  ========================================================= */
  const handleGoToDoctorProfileById = useCallback(
    (doctorId) => {
      const id = normId(doctorId);
      if (!id) return;
      navigate(doctorProfilePath(id));
    },
    [navigate]
  );

  const handleGoToCommentProfile = useCallback(
    (comment) => {
      const { id, isDoctor } = extractAuthorIdAndRole(comment);
      if (!id || !isDoctor) return;
      navigate(doctorProfilePath(id));
    },
    [navigate]
  );

  /* =========================================================
     MENU 3 POINTS (POST)
  ========================================================= */
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const editInputRef = useRef(null);
  const [editingPost, setEditingPost] = useState(false);
  const [editingPostValue, setEditingPostValue] = useState(content || "");
  const [savingPost, setSavingPost] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canEdit = canManagePost && typeof onEdit === "function";
  const canDelete = canManagePost && typeof onDelete === "function";

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  const handleMenuReport = useCallback(() => closeMenu(), [closeMenu]);
  const handleMenuSave = useCallback(async () => {
    if (!postKey || typeof onToggleSave !== "function" || isSaving) return;
    try {
      await onToggleSave(postKey);
      closeMenu();
    } catch {
      // The parent exposes the error state; keep the menu open for retry.
    }
  }, [postKey, onToggleSave, isSaving, closeMenu]);

  const handleMenuEdit = useCallback(() => {
    closeMenu();
    if (!canEdit || !postKey) return;
    setEditingPostValue(content || "");
    setEditingPost(true);
  }, [closeMenu, canEdit, postKey, content]);

  useEffect(() => {
    if (editingPost) editInputRef.current?.focus();
  }, [editingPost]);

  const cancelPostEdit = useCallback(() => {
    if (savingPost) return;
    setEditingPost(false);
    setEditingPostValue(content || "");
  }, [content, savingPost]);

  const savePostEdit = useCallback(async () => {
    const nextText = editingPostValue.trim();
    if (!nextText || !canEdit || !postKey || savingPost) return;

    try {
      setSavingPost(true);
      await onEdit?.(postKey, { content: nextText });
      setEditingPost(false);
    } finally {
      setSavingPost(false);
    }
  }, [editingPostValue, canEdit, postKey, savingPost, onEdit]);

  const handleMenuDelete = useCallback(() => {
    closeMenu();
    if (!canDelete || !postKey) return;
    setDeleteOpen(true);
  }, [closeMenu, canDelete, postKey]);

  const confirmPostDelete = useCallback(async () => {
    if (!canDelete || !postKey || isDeleting) return;
    await onDelete?.(postKey);
    setDeleteOpen(false);
  }, [canDelete, postKey, isDeleting, onDelete]);

  /* =========================================================
     MENU 3 POINTS (COMMENTS) + EDIT INLINE
  ========================================================= */
  const [openCommentMenuId, setOpenCommentMenuId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  useEffect(() => {
    if (!openCommentMenuId) return;
    const onDown = (e) => {
      const inside = e.target.closest?.(`[data-comment-menu="${openCommentMenuId}"]`);
      if (!inside) setOpenCommentMenuId(null);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpenCommentMenuId(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openCommentMenuId]);

  const startEditComment = useCallback((commentId, initialText) => {
    setEditingCommentId(String(commentId));
    setEditingValue(safeStr(initialText));
    setOpenCommentMenuId(null);
  }, []);

  const cancelEditComment = useCallback(() => {
    setEditingCommentId(null);
    setEditingValue("");
  }, []);

  const saveEditComment = useCallback(
    async (commentId) => {
      const cid = String(commentId);
      const newTxt = safeStr(editingValue);
      if (!cid || !newTxt) return;
      try {
        if (typeof updateComment === "function") {
          const actorId = effectiveActorId;
          await updateComment({
            commentId: cid,
            content: newTxt,
            postId: postKey,
            ...(viewerIsDoctor
              ? { doctorId: String(actorId) }
              : { userId: String(actorId) }),
          });
        }
      } finally {
        cancelEditComment();
        if (typeof fetchComments === "function") fetchComments().catch(() => {});
      }
    },
    [editingValue, updateComment, effectiveActorId, postKey, fetchComments, cancelEditComment]
  );

  const handleDeleteComment = useCallback(
    async (commentId) => {
      const cid = String(commentId);
      if (!cid) return;
      const ok = window.confirm(t("posts.confirmDeleteComment", "Delete this comment?"));
      if (!ok) return;
      try {
        if (typeof deleteComment === "function") {
          await deleteComment({ commentId: cid, postId: postKey });
        }
      } finally {
        setOpenCommentMenuId(null);
        if (typeof fetchComments === "function") fetchComments().catch(() => {});
      }
    },
    [deleteComment, postKey, fetchComments, t]
  );

  /* =========================================================
     Cache LOCAL des auteurs
  ========================================================= */
  const [authorsCache, setAuthorsCache] = useState({});
  const cacheRef = useRef(new Map());
  const pendingRef = useRef(new Set());

  const putCache = useCallback((key, value) => {
    cacheRef.current.set(key, value);
    setAuthorsCache((prev) => (prev[key] ? prev : { ...prev, [key]: value }));
  }, []);

  const fetchDoctor = useCallback(
    async (doctorId) => {
      const id = String(doctorId);
      const key = `doctor:${id}`;
      if (cacheRef.current.has(key)) return;
      if (pendingRef.current.has(key)) return;
      pendingRef.current.add(key);
      try {
        const res = await axiosDoctorRegister.get(`/${encodeURIComponent(id)}`, {
          withCredentials: true,
          timeout: 7000,
        });
        const d = res?.data?.data ?? res?.data ?? null;
        if (!d) return;
        putCache(key, {
          role: "doctor",
          firstName: d?.firstName || d?.user?.firstName || d?.prenom || "",
          lastName: d?.lastName || d?.user?.lastName || d?.nom || "",
          name: d?.fullName || d?.name || d?.displayName || null,
          avatar:
            d?.avatarUrl ||
            d?.avatar ||
            d?.profileImage ||
            d?.user?.avatar ||
            d?.user?.avatarUrl ||
            null,
        });
      } catch {
      } finally {
        pendingRef.current.delete(key);
      }
    },
    [putCache]
  );

  const fetchUser = useCallback(
    async (userId) => {
      const id = String(userId);
      const key = `user:${id}`;
      if (cacheRef.current.has(key)) return;
      if (pendingRef.current.has(key)) return;
      pendingRef.current.add(key);
      try {
        const res = await axiosUsers.get(`/${encodeURIComponent(id)}`, {
          withCredentials: true,
          timeout: 7000,
        });
        const u = res?.data?.data ?? res?.data ?? null;
        if (!u) return;
        putCache(key, {
          role: "patient",
          firstName: u?.firstName || u?.prenom || "",
          lastName: u?.lastName || u?.nom || "",
          name: u?.fullName || u?.name || u?.displayName || null,
          avatar: u?.avatarUrl || u?.avatar || u?.profileImage || u?.photo || null,
        });
      } catch {
      } finally {
        pendingRef.current.delete(key);
      }
    },
    [putCache]
  );

  useEffect(() => {
    if (!Array.isArray(comments) || comments.length === 0) return;
    for (const c of comments) {
      const { id, isDoctor } = extractAuthorIdAndRole(c);
      if (!id) continue;
      const cacheKey = `${isDoctor ? "doctor" : "user"}:${String(id)}`;
      if (cacheRef.current.has(cacheKey)) continue;
      if (isDoctor) fetchDoctor(id);
      else fetchUser(id);
    }
  }, [comments, fetchDoctor, fetchUser]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!postKey) return;

    const formEl = e.currentTarget;
    const txt = safeStr(formEl?.comment?.value);
    if (!txt) return;

    const actorId = effectiveActorId;
    if (!actorId) {
      console.warn("⚠️ handleSubmitComment: actorId manquant");
      return;
    }

    if (!resolvedPostOwnerId) {
      console.warn(
        "⚠️ handleSubmitComment: postOwnerId manquant. " +
          "Le backend va tenter MS_POST_SERVICE_URL et risque de renvoyer 400. " +
          "Vérifier que FeedDoctor.jsx passe bien postOwnerId={post.doctorId ?? post.userId} à PostCard."
      );
    }

    const dto = {
      content: txt,
      postId: postKey,
      ...(resolvedPostOwnerId ? { postOwnerId: resolvedPostOwnerId } : {}),
      ...(viewerIsDoctor
        ? { doctorId: String(actorId) }
        : { userId: String(actorId) }),
    };


    await addComment(dto);
    formEl?.reset?.();
    if (!showInlineComments) setShowInlineComments(true);
  };

  const hasDocument = Array.isArray(attachments) && attachments.some((a) => a.type === "document");

  const buildPdfUrl = (url) => {
    if (!documentFitH || !url) return url;
    const [base, hash = ""] = String(url).split("#");
    const params = new URLSearchParams(hash);
    if (!params.has("view")) params.set("view", "FitH");
    if (!params.has("toolbar")) params.set("toolbar", "0");
    if (!params.has("navpanes")) params.set("navpanes", "0");
    if (!params.has("scrollbar")) params.set("scrollbar", "0");
    return `${base}#${params.toString()}`;
  };

  const commentsPropInt = Number.isFinite(Number(commentsProp)) ? Number(commentsProp) : null;

  const totalCommentCount = useMemo(() => {
    if (typeof commentsCount === "number") return commentsCount;
    if (Array.isArray(comments) && comments.length > 0) return comments.length;
    return commentsPropInt ?? 0;
  }, [commentsCount, comments, commentsPropInt]);

  const displayDoctorName = formatDoctorName(doctorName);

  return (
    <div className={`post-card ${hasDocument && documentFullBleed ? "doc-bleed" : ""}`}>
      <div className="post-header">
        <div className="profile-section">
          {headerAvatar && headerAvatar !== DEFAULT_AVATAR ? (
            <img
              src={headerAvatar}
              alt="Dr"
              className="dr-avatar-icon"
              onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
              style={{ borderRadius: "50%", objectFit: "cover", cursor: "pointer" }}
              onClick={() => handleGoToDoctorProfileById(postOwnerId)}
            />
          ) : (
            <div
              className="dr-avatar-fallback"
              onClick={() => handleGoToDoctorProfileById(postOwnerId)}
              style={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                background: "#DBEAFE",
                color: "#fff",
                fontSize: 18,
                fontWeight: 600,
                fontFamily: "Poppins, Arial, sans-serif",
                letterSpacing: 1,
                userSelect: "none",
                border: "transparent",
                cursor: "pointer",
              }}
            >
              {getInitials(doctorName, "DR")}
            </div>
          )}

          <div className="profile-info">
            <div className="post-title-row">
              <h3
                className="doctor-name"
                onClick={() => handleGoToDoctorProfileById(postOwnerId)}
                style={{ cursor: "pointer" }}
              >
                {displayDoctorName}
              </h3>

              {viewerIsDoctor && visibleToDoctorsOnly && (
                <span
                  className="post-visibility-badge"
                  title={t("posts.doctorsOnlyHint", "Visible for doctors only")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="preview-icon"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="16" r="1" />
                    <rect x="3" y="10" width="18" height="12" rx="2" />
                    <path d="M7 10V7a5 5 0 0 1 10 0v3" />
                  </svg>
                  {t("posts.doctorsOnly", "Doctors only")}
                </span>
              )}
            </div>

            <p className="post-time">
              {t("homePage.postTime")} {timeLabel || timeAgo(createdAt) || t("postCard.now")}
            </p>
          </div>
        </div>

        <div className="pcard-menu-wrapper" ref={menuRef}>
          <button
            className="pcard-menu-btn"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((p) => !p);
            }}
            type="button"
            title={t("postCard.options", "Options")}
            disabled={!!isUpdating}
          >
            <span className="pcard-menu-dots">•••</span>
          </button>

          <div className={`pcard-menu ${menuOpen ? "open" : ""}`} role="menu">
            {viewerIsDoctor && typeof onToggleSave === "function" && (
              <button className="pcard-menu-item" role="menuitem" onClick={handleMenuSave} disabled={isSaving}>
                {isSaving ? t("common.loading", "Loading...") : isSaved ? t("postCard.unsave", "Unsave") : t("postCard.savePost", "Save")}
              </button>
            )}
            {(canEdit || canDelete) && <div className="pcard-menu-sep" />}
            {canEdit && (
              <button
                className="pcard-menu-item"
                role="menuitem"
                onClick={handleMenuEdit}
                disabled={!!isUpdating}
              >
                {t("postCard.edit", "Edit")}
              </button>
            )}
            {canDelete && (
              <button
                className="pcard-menu-item danger"
                role="menuitem"
                onClick={handleMenuDelete}
                disabled={!!isUpdating}
              >
                {t("postCard.delete", "Delete")}
              </button>
            )}
            <div className="pcard-menu-sep" />
            <button className="pcard-menu-item danger" role="menuitem" onClick={handleMenuReport}>
              {t("postCard.report", "Report")}
            </button>
          </div>
        </div>
      </div>

      <div className="post-content">
        {editingPost ? (
          <div className="post-edit-area">
            <textarea
              ref={editInputRef}
              className="post-edit-input"
              value={editingPostValue}
              onChange={(event) => setEditingPostValue(event.target.value)}
              disabled={savingPost || isUpdating}
              aria-label={t("postCard.editPrompt", "Edit post content")}
            />
            <div className="post-edit-actions">
              <button
                type="button"
                className="post-edit-cancel"
                onClick={cancelPostEdit}
                disabled={savingPost || isUpdating}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className="post-edit-save"
                onClick={savePostEdit}
                disabled={!editingPostValue.trim() || savingPost || isUpdating}
              >
                {savingPost || isUpdating
                  ? t("postCard.updating", "Saving...")
                  : t("postCard.save", "Save")}
              </button>
            </div>
          </div>
        ) : (
          <p className="post-text">{content}</p>
        )}
      </div>

      {isFirstPost ? (
        <div className="post-image">
          <img src={PostContentIcon} alt={t("postCard.postMediaAlt")} className="postcontent-image" />
        </div>
      ) : attachments && attachments.length > 0 ? (
        <div className="post-image post-image--auto">
          {attachments.map((att) => {
            if (att.type === "image" && (att.preview || att.url)) {
              return (
                <div key={att.id} className="attachment-item">
                  <img src={att.preview || att.url} alt={att.name || t("postCard.imageAlt")} className="preview-img" />
                </div>
              );
            }
            if (att.type === "video" && (att.url || att.preview)) {
              return (
                <div key={att.id} className="attachment-item">
                  <video src={att.url || att.preview} controls className="preview-video" preload="metadata" />
                </div>
              );
            }
            if (att.type === "document" && (att.url || att.preview)) {
              const pdfUrl = buildPdfUrl(att.url || att.preview);
              const docH = documentFullBleed ? `${documentViewportHeight}vh` : `${documentHeight}px`;
              return (
                <div key={att.id} className="attachment-item">
                  <div className="doc-wrapper" style={{ "--doc-h": docH }}>
                    <iframe src={pdfUrl} title={att.name || t("postCard.document")} className="preview-document" loading="lazy" />
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      ) : (
        image && (
          <div className="post-image post-image--auto">
            <img src={image} alt={t("postCard.postMediaAlt")} />
          </div>
        )
      )}



   {viewerIsDoctor && (
      <div className="post-actions">
        <button
          className={`action-button like-button ${liked ? "liked" : ""}`}
          onClick={handleToggleLike}
          disabled={!postKey || !effectiveActorId}
          aria-pressed={!!liked}
          type="button"
        >
          <img src={liked ? likeIconFilled : likeIconOutline} alt="" aria-hidden="true" width="16" height="17" />
          <span>{t("homePage.likePost")}</span>
        </button>


        <button
          type="button"
          className="action-button comment-button"
          onClick={() => setShowInlineComments((v) => !v)}
          aria-expanded={showInlineComments}
          disabled={!postKey}
        >
          <img src={commentIconPrimary} alt="" aria-hidden="true" width="16" height="16" />
          <span>{t("homePage.comment")}</span>
        </button>

        <div className="engagement-stats">
          <button
            type="button"
            className="likes-count-btn"
            onClick={handleToggleLike}
            disabled={!postKey || !effectiveActorId}
          >
            <img src={liked ? likeIconFilled : likeIconOutline} alt="" aria-hidden="true" width="16" height="17" />
            <span className="likes-count">{Number.isFinite(Number(likeCount)) ? likeCount : likes ?? 0}</span>
          </button>

          <button
            type="button"
            className="comments-count-btn"
            onClick={() => setShowInlineComments((v) => !v)}
            aria-expanded={showInlineComments}
          >
            <img src={commentIconMuted} alt="" aria-hidden="true" width="16" height="16" />
            <span className="comments-count">{totalCommentCount}</span>
          </button>
        </div>
        
      </div>

         )}
         
      {showInlineComments && postKey && (
        <div className="pc-comments">
          <div className="pcc-composer">
            <form className="pcc-composer-form" onSubmit={handleSubmitComment}>
              <textarea
                name="comment"
                placeholder={t("homePage.writeComment")}
                required
                rows={1}
                onInput={(e) => {
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
              />
              <button type="submit" aria-label={t("postCard.sendComment")} title={t("postCard.sendComment")}>
                <img className="pcc-soft-icon" src={sendIcon} alt="" aria-hidden="true" />
              </button>
            </form>
          </div>

          <div className="pcc-list">
            {orderedComments.length > 0 ? (
              orderedComments.map((c) => {
                const cid = String(c?._id || c?.id || "");
                const { id: authorId, isDoctor } = extractAuthorIdAndRole(c);

                const cacheKey = authorId ? `${isDoctor ? "doctor" : "user"}:${String(authorId)}` : null;
                const author = (cacheKey && (authorsCache[cacheKey] || cacheRef.current.get(cacheKey))) || null;

                const rawFullName = buildFullName(author || c?.user);
                const fullName = isDoctor ? formatDoctorName(rawFullName) : rawFullName;

                const rawAvatar =
                  author?.avatar ||
                  c?.user?.avatar ||
                  c?.user?.avatarUrl ||
                  c?.user?.profileImage ||
                  c?.user?.photo ||
                  null;

                const avatar = isDoctor
                  ? resolveDoctorAvatarSafe(rawAvatar)
                  : resolvePatientAvatarSafe(rawAvatar);

                const canManageThisComment =
                  !!cid &&
                  !!effectiveActorId &&
                  !!authorId &&
                  String(normId(authorId)) === String(normId(effectiveActorId));

                const isEditing =
                  editingCommentId && cid && String(editingCommentId) === String(cid);

                return (
                  <div key={cid || Math.random()} className="pcc-item">
                    <div className="pcc-left">
                      {avatar ? (
                        <img
                          className="pcc-av"
                          src={avatar}
                          alt={fullName}
                          onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                          onClick={() => isDoctor && handleGoToCommentProfile(c)}
                          style={{ cursor: isDoctor ? "pointer" : "default" }}
                        />
                      ) : (
                        <div
                          className="pcc-av pcc-av-default"
                          onClick={() => isDoctor && handleGoToCommentProfile(c)}
                          style={{ cursor: isDoctor ? "pointer" : "default" }}
                        >
                          {isDoctor ? getInitials(rawFullName, "DR") : (fullName || "U").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    <div className="pcc-body">
                      <div className="pcc-header">
                        <div className="pcc-info">
                          <span
                            className="pcc-name"
                            onClick={() => isDoctor && handleGoToCommentProfile(c)}
                            style={{ cursor: isDoctor ? "pointer" : "default" }}
                          >
                            {fullName}
                          </span>
                          <span className="pcc-role">{isDoctor ? "doctor" : "patient"}</span>
                          <span className="pcc-time">{timeAgo(c.createdAt)}</span>
                        </div>

                        {canManageThisComment && (
                          <div className="pcc-menu-wrapper" data-comment-menu={cid}>
                            <button
                              type="button"
                              className="pcc-menu-btn"
                              aria-haspopup="menu"
                              aria-expanded={openCommentMenuId === cid}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenCommentMenuId((prev) => (prev === cid ? null : cid));
                              }}
                              title={t("postCard.options", "Options")}
                              disabled={!!isUpdating}
                            >
                              •••
                            </button>

                            <div className={`pcc-menu ${openCommentMenuId === cid ? "open" : ""}`} role="menu">
                              <button
                                className="pcc-menu-item"
                                role="menuitem"
                                onClick={() => startEditComment(cid, c?.content || c?.text || "")}
                                disabled={!!isUpdating}
                              >
                                {t("postCard.edit", "Edit")}
                              </button>
                              <button
                                className="pcc-menu-item danger"
                                role="menuitem"
                                onClick={() => handleDeleteComment(cid)}
                                disabled={!!isUpdating}
                              >
                                {t("postCard.delete", "Delete")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {isEditing ? (
                        <div className="pcc-edit">
                          <textarea
                            className="pcc-edit-input"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            rows={2}
                            autoFocus
                          />
                          <div className="pcc-edit-actions">
                            <button
                              type="button"
                              className="pcc-edit-btn"
                              onClick={() => saveEditComment(cid)}
                              disabled={!safeStr(editingValue) || !!isUpdating}
                            >
                              {t("common.save", "Save")}
                            </button>
                            <button
                              type="button"
                              className="pcc-edit-btn ghost"
                              onClick={cancelEditComment}
                              disabled={!!isUpdating}
                            >
                              {t("common.cancel", "Cancel")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="pcc-text">{c.content || c.text}</div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : !loadingComments ? (
              <div className="pcc-empty">{t("homePage.noComments")}</div>
            ) : null}

            {loadingComments && (
              <div className="pcc-more">{t("homePage.loadingComments")}</div>
            )}
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={deleteOpen}
        loading={isDeleting}
        onConfirm={confirmPostDelete}
        onClose={() => !isDeleting && setDeleteOpen(false)}
      />
    </div>
  );
};

export default PostCard;